import { useState, useEffect, useRef } from "react";
import { getSfObjectsForPhoneBookSearch } from "../utils/callCenterSettings";

const MIN_CHARS = 2;

/**
 * Parses the raw OpenCTI runApex callback response from unrc.UnaricUnifyPhonebookSearch.
 * Returns { error: string|null, results: Array }.
 * Exported as a pure function for unit testing.
 */
export function parseApexResponse(response) {
  if (!response) {
    return { error: "Unable to process search results", results: [] };
  }

  if (!response.success) {
    const detail =
      Array.isArray(response.errors) && response.errors.length
        ? response.errors
            .map((e) => e?.description || e?.code || "Unknown error")
            .join("; ")
        : "Unknown error";
    void detail; // callers are responsible for logging
    return { error: "Failed to fetch search results", results: [] };
  }

  try {
    const raw = response?.returnValue?.runApex;
    if (raw == null) {
      return { error: "Unable to process search results", results: [] };
    }
    const parsed = JSON.parse(raw);

    if (parsed?.code >= 400) {
      return {
        error: "Salesforce search not configured. Contact your Salesforce administrator",
        results: [],
      };
    }

    return {
      error: null,
      results: Array.isArray(parsed?.results) ? parsed.results : [],
    };
  } catch {
    return { error: "Unable to process search results", results: [] };
  }
}

/**
 * Flattens Salesforce phonebook results into one flat row per phone number.
 * Skips entries where phoneNumber is falsy.
 * Exported as a pure function for unit testing.
 *
 * @param {Array} results - Array of result objects from usePhoneSearch.
 * @returns {Array<{ recordId, recordType, name, company, phone, label }>}
 */
export function buildSfSearchRows(results) {
  const rows = [];
  for (const record of results) {
    for (const p of record?.phones ?? []) {
      if (!p?.phoneNumber) continue;
      rows.push({
        recordId: record?.recordId,
        recordType: record?.recordType,
        name: record?.name,
        company: record?.company,
        phone: p?.phoneNumber,
        label: p?.label,
      });
    }
  }
  return rows;
}

/**
 * OpenCTI Apex-based search hook for Salesforce phone/name lookup.
 *
 * Search fires only when `query` changes to a value of MIN_CHARS or more —
 * the caller is responsible for setting query only on explicit user action
 * (e.g. clicking the search icon), not on every keystroke.
 *
 * @param {string} query - The committed search term.
 * @returns {{ results: Array, isLoading: boolean, error: string|null }}
 *
 * Each result item:
 *   { recordId, recordType, name, company, phones: [{ phoneNumber, label }] }
 */
export function usePhoneSearch(query) {
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Tracks whether the effect has been superseded by a newer query
  const staleRef = useRef(false);

  useEffect(() => {
    // Clear state when query is too short
    if (!query || query.length < MIN_CHARS) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    // Guard: Salesforce OpenCTI must be available
    if (
      typeof sforce === "undefined" ||
      typeof sforce?.opencti?.runApex !== "function"
    ) {
      setError("Salesforce OpenCTI is not available");
      setResults([]);
      setIsLoading(false);
      return;
    }

    staleRef.current = false;
    setIsLoading(true);
    setError(null);
    setResults([]);
    setHasSearched(false);
    const sfObjects = getSfObjectsForPhoneBookSearch();
    const objectsToSearch = sfObjects.length > 0 ? "(" + sfObjects.join(", ") + ")" : "";
    sforce.opencti.runApex({
      apexClass: "unrc.UnaricUnifyPhonebookSearch",
      methodName: "searchPhonebook",
      methodParams: "searchTerm=" + query + "&objectsToSearch=" + objectsToSearch,
      callback: (response) => {
        // Discard if a newer search has already started
        if (staleRef.current) return;

        setIsLoading(false);

        const { error, results } = parseApexResponse(response);

        if (error) {
          console.error("[usePhoneSearch] response error:", error, response);
        }

        setError(error);
        setResults(results);
        setHasSearched(true);
      },
    });

    return () => {
      // Mark this invocation stale so its callback is ignored
      staleRef.current = true;
    };
  }, [query]);

  return { results, isLoading, error, hasSearched };
}
